import { useRef } from 'react';

import logScreenView from '@zr/utilities/logScreenView';
import logScreenInteraction from '@zr/utilities/telemetry/logScreenInteraction';

import getFlowSuperProperties from '@zr/jobseeker-public/apps/jobseeker-screens/components/Screens/ScreenTypes/getFlowSuperProperties';

/**
 * This telemetry hook keeps track of super properties, view index, and added properties throughout callbacks when we want to log screen events
 * Callers will generally build view and interaction events, then commit to logging the view and interaction at some point later on
 * Telemetry Sheet: https://docs.google.com/spreadsheets/d/1GZfOsbY28NGIoFuXXAVBbrW9cSZ_1Y7zJscJlQbAL28/edit#gid=100473960
 *
 * @param {string} screenType the screen type name to log
 * @param {object} applyData flow parameters specified in applyFlowPropTypes
 * @returns object containing logging functions
 */
export default function useUploadResumeOrCreateProfileTelemetry(
  screenType,
  applyData,
) {
  // super properties always sent in every screen event
  const superProperties = getFlowSuperProperties(applyData);

  // for each screen view, log the screen view index and increment after each one
  const screenViewIndex = useRef(1);

  // added screen view properties are added here
  const extraScreenViewProperties = useRef({});

  const addViewProperties = (extraViewProperties) => {
    extraScreenViewProperties.current = {
      ...extraScreenViewProperties.current,
      ...extraViewProperties,
    };
  };

  const logView = () => {
    logScreenView(applyData.areaName, screenType, {
      ...superProperties,
      ...extraScreenViewProperties.current,
      screen_view_index: screenViewIndex.current,
    });
    // increment index for the next screen view
    screenViewIndex.current += 1;
    // clear extra screen view properties for the next one
    extraScreenViewProperties.current = {};
  };

  // added screen interaction properties are added here
  const extraScreenInteractionProperties = useRef({});

  const addInteractionProperties = (extraViewProperties) => {
    extraScreenInteractionProperties.current = {
      ...extraScreenInteractionProperties.current,
      ...extraViewProperties,
    };
  };

  const logInteraction = () => {
    logScreenInteraction(applyData.areaName, screenType, {
      ...superProperties,
      ...extraScreenInteractionProperties.current,
    });
    // clear extra screen interaction properties for the next one
    extraScreenInteractionProperties.current = {};
  };

  return {
    addViewProperties,
    logView,
    addInteractionProperties,
    logInteraction,
  };
}
